import type { BuildDetalhe, BuildResumo, StatusBuild } from '@/data/types';

/**
 * Regras de capability e de transição, num lugar só.
 *
 * A API é quem decide de verdade (403/409/422); estas funções existem para a
 * interface mostrar ANTES do clique por que uma ação não está disponível — o
 * botão fica visível, bloqueado, com o motivo no tooltip (padrão
 * `PermissionButton` herdado do PAN). Nenhuma tela decide sozinha.
 */

export const CAPABILITY_MAINTAINER = 'maintainer';
export const CAPABILITY_PAINEL = 'painel';
export const CAPABILITY_ADMIN = 'admin';
export const CAPABILITY_SYSADMIN = 'sysadmin';

export interface MotivoBloqueio {
  permitido: boolean;
  /** Frase pronta, em PT-BR, que vai para tooltip/`aria-label`. Vazia quando permitido. */
  motivo: string;
}

const LIBERADO: MotivoBloqueio = { permitido: true, motivo: '' };

/**
 * `sysadmin` é super role: passa em tudo. `admin` dá acesso a tudo do dia a dia
 * (vale como `maintainer` e como `painel`). Só conceder/revogar `sysadmin` e
 * apagar incidente exigem `sysadmin` de verdade — essas checam a role direto,
 * não por aqui.
 */
export function temCapability(roles: string[], capability: string): boolean {
  if (roles.includes(capability) || roles.includes(CAPABILITY_SYSADMIN)) return true;
  return roles.includes(CAPABILITY_ADMIN) && capability !== CAPABILITY_SYSADMIN;
}

export function podeHomologar(roles: string[]): MotivoBloqueio {
  return temCapability(roles, CAPABILITY_MAINTAINER)
    ? LIBERADO
    : { permitido: false, motivo: 'Mudar o status de homologação exige a capability "maintainer" (ou "admin").' };
}

/** Item "Usuários" (permissionamento) do menu do usuário: só para quem tem `admin` (ou `sysadmin`). */
export function podeAdministrar(roles: string[]): MotivoBloqueio {
  return temCapability(roles, CAPABILITY_ADMIN)
    ? LIBERADO
    : { permitido: false, motivo: 'Administrar capabilities exige a capability "admin".' };
}

export function podePublicarNoPainel(roles: string[]): MotivoBloqueio {
  return temCapability(roles, CAPABILITY_PAINEL)
    ? LIBERADO
    : { permitido: false, motivo: 'Publicar no painel do cliente exige a capability "painel" (ou "admin").' };
}

export const ROTULO_STATUS: Record<StatusBuild, string> = {
  nao_homologado: 'Não homologada',
  homologado: 'Homologada',
  reprovado: 'Reprovada',
};

export interface AcaoStatus {
  destino: StatusBuild;
  rotulo: string;
  /** Só `reprovado` exige motivo (o contrato devolve 422 sem ele). */
  exigeMotivo: boolean;
  /** Reprovar uma versão já homologada: é uma demoção, o diálogo explica isso. */
  democao: boolean;
  bloqueio: MotivoBloqueio;
}

export function exigeMotivo(status: StatusBuild): boolean {
  return status === 'reprovado';
}

const HOMOLOGAR = { destino: 'homologado', rotulo: 'Homologar', exigeMotivo: false, democao: false } as const;
const REPROVAR = { destino: 'reprovado', rotulo: 'Reprovar', exigeMotivo: true, democao: false } as const;

/** Ações oferecidas na UI a partir de cada estado (a API aceita qualquer transição para outro status). */
const ACOES_POR_STATUS: Record<StatusBuild, Array<Omit<AcaoStatus, 'bloqueio'>>> = {
  nao_homologado: [HOMOLOGAR, REPROVAR],
  homologado: [{ ...REPROVAR, democao: true }],
  reprovado: [HOMOLOGAR, { destino: 'nao_homologado', rotulo: 'Voltar para não homologada', exigeMotivo: false, democao: false }],
};

/**
 * Ações de status disponíveis para uma versão, conforme o estado atual. Cada
 * uma vem com o bloqueio já resolvido — capability primeiro, depois a regra do
 * painel (a API devolve 409 ao tirar do `homologado` a versão que está no
 * painel do cliente).
 */
export function acoesDeStatus(build: Pick<BuildResumo, 'status' | 'no_painel'>, roles: string[]): AcaoStatus[] {
  const capability = podeHomologar(roles);
  return ACOES_POR_STATUS[build.status].map((acao) => {
    if (!capability.permitido) return { ...acao, bloqueio: capability };
    if (build.no_painel && build.status === 'homologado') {
      return {
        ...acao,
        bloqueio: {
          permitido: false,
          motivo: 'Esta versão está no painel do cliente. Publique outra versão no painel antes de mudar o status dela.',
        },
      };
    }
    return { ...acao, bloqueio: LIBERADO };
  });
}

/** Pode publicar ESTA versão no painel? Capability, depois `elegivel_painel` da API. */
export function bloqueioPublicacao(
  build: Pick<BuildDetalhe, 'no_painel' | 'elegivel_painel'>,
  roles: string[],
): MotivoBloqueio {
  const capability = podePublicarNoPainel(roles);
  if (!capability.permitido) return capability;
  if (build.no_painel) return { permitido: false, motivo: 'Esta já é a versão atual do painel do cliente.' };
  if (!build.elegivel_painel.ok) {
    return {
      permitido: false,
      motivo: build.elegivel_painel.motivo ?? 'A API informou que esta versão não é elegível para o painel.',
    };
  }
  return LIBERADO;
}

/**
 * Elegível para o seletor da tela do painel (espelho do que a API valida no
 * `POST /v1/painel`): homologada, branch `v2.*` e versão `2.RRRR.N.0`.
 */
export function elegivelParaPainel(build: Pick<BuildResumo, 'status' | 'branch' | 'versao'>): boolean {
  return build.status === 'homologado' && build.branch.startsWith('v2.') && /^2\.\d{4}\.\d+\.0$/.test(build.versao);
}
